import AdminTable from '../../../components/admin/AdminTable';
import React, { useState, useEffect } from 'react';
import { fetchPagosByCita, fetchSesionesByCita, fetchPagosPorSesion } from '../../../services/pagosService';
import { fetchCitaById } from '../../../services/citasService';

import { X } from 'lucide-react';

interface ListadoPagosModalProps {
    isOpen: boolean;
    onClose: () => void;
    citaId: number;
}

const PagosModalHistorial: React.FC<ListadoPagosModalProps> = ({
    isOpen,
    onClose,
    citaId
}) => {
    const [citaCodigo, setCitaCodigo] = useState<string>(''); // Almacenamos el código de la cita
    const [pagosCitas, setPagosCitas] = useState<any[]>([]);
    const [pagosSesiones, setPagosSesiones] = useState<any[]>([]);
    const [activeTab, setActiveTab] = useState<'citas' | 'sesiones'>('citas');
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (isOpen && citaId) {
            const cargarPagos = async () => {
                setIsLoading(true);
                try {
                    // Obtener los detalles de la cita (incluyendo el código)
                    const citaData = await fetchCitaById(citaId); // Esta función debe retornar la cita con el código
                    setCitaCodigo(citaData.codigo); // Guardamos el código de la cita

                    // Pagos de la cita
                    const citasPagos = await fetchPagosByCita(citaId);
                    setPagosCitas(citasPagos);

                    // Pagos de las sesiones de esa cita
                    const sesiones = await fetchSesionesByCita(citaId);
                    let todosPagosSesiones: any[] = [];
                    for (const sesion of sesiones) {
                        const pagosSesion = await fetchPagosPorSesion(sesion.id);
                        todosPagosSesiones = todosPagosSesiones.concat(pagosSesion);
                    }
                    setPagosSesiones(todosPagosSesiones);

                } catch (error) {
                    /* Browser diagnostics omitted. */
                } finally {
                    setIsLoading(false);
                }
            };
            cargarPagos();
        }
    }, [isOpen, citaId]);

    const renderTablaPagos = (pagos: any[]) => {
        if (isLoading) {
            return (
                <div className="flex justify-center items-center h-40">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
                </div>
            );
        }

        if (pagos.length === 0) {
            return (
                <div className="text-center py-10">
                    <p className="text-gray-500">No se encontraron pagos registrados</p>
                </div>
            );
        }

        return (
            <AdminTable className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Fecha
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Tipo
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Monto (Bs)
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Método
                        </th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {pagos.map((pago) => (
                        <tr key={pago.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {pago.codigo}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {new Date(pago.fecha_pago).toLocaleDateString('es-BO')}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {pago.sesion ? 'Sesión' : 'Cita'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {Number(pago.monto).toFixed(2)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {pago.tipo_pago_nombre || 'N/A'}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </AdminTable>
        );
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                <div className="border-b p-4 flex justify-between items-center">
                    <h3 className="text-xl font-bold text-gray-800">
                        Historial de Pagos - Cita {citaCodigo} {/* Mostramos el código aquí */}
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-800"
                    >
                        <X className="h-6 w-6" />
                    </button>
                </div>

                <div className="p-4">
                    {/* Pestañas */}
                    <div className="flex border-b mb-4">
                        <button
                            className={`py-2 px-4 font-medium ${activeTab === 'citas'
                                ? 'text-indigo-600 border-b-2 border-indigo-600'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                            onClick={() => setActiveTab('citas')}
                        >
                            Pagos de Citas ({pagosCitas.length})
                        </button>
                        <button
                            className={`py-2 px-4 font-medium ${activeTab === 'sesiones'
                                ? 'text-indigo-600 border-b-2 border-indigo-600'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                            onClick={() => setActiveTab('sesiones')}
                        >
                            Pagos de Sesiones ({pagosSesiones.length})
                        </button>
                    </div>

                    {/* Contenido de pestañas */}
                    {activeTab === 'citas' ? (
                        <div>
                            <h4 className="text-lg font-semibold mb-4">Pagos de Citas</h4>
                            {renderTablaPagos(pagosCitas)}
                        </div>
                    ) : (
                        <div>
                            <h4 className="text-lg font-semibold mb-4">Pagos de Sesiones</h4>
                            {renderTablaPagos(pagosSesiones)}
                        </div>
                    )}
                </div>

                <div className="border-t p-4 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                    >
                        Cerrar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PagosModalHistorial;

